/**
 * Phase 2 — rank & predict (SPEC §2.3), simultaneous and untimed.
 *
 * The screen is asymmetric by design and that asymmetry is the whole reason
 * prediction exists (SPEC §1): without it, eleven people watch one person drag
 * cards. Turn player gets the ranking board; everyone else gets two picks they
 * can change as often as they like.
 *
 * **The turn player's live count is gone, and that is a reversal.** This screen
 * carried "7 of 11 predictions in" under the submit action, on the argument
 * that it told them when the room was ready. It never informed a decision — it
 * only invited them to hold a finished ranking hostage to somebody else's two
 * taps, which is the dead-screen problem prediction exists to solve, pointed
 * the other way. SPEC §2.3 puts the clock in their hands twice over; they
 * submit when *they* are ready, and the server no longer sends them the frame
 * at all.
 */
import { useEffect, useState } from 'react';
import {
  Screen,
  WaitingFor,
  useRankingInteraction,
  type RankingCandidate,
} from '../../components/index.js';
import {
  useQuestion,
  useRankablePlayers,
  useIsTurnPlayer,
  useRoomCode,
  useRoomError,
  useSubmitRanking,
  useTurn,
} from '../../store/index.js';
import { SkipOffer } from './QuestionSelectScreen.js';
import { PredictionFlow } from './PredictionPad.js';
import { loadRankingDraft, saveRankingDraft } from './rankingDraft.js';

export function RankAndPredictScreen() {
  const isTurnPlayer = useIsTurnPlayer();
  return isTurnPlayer ? <RankSide /> : <PredictSide />;
}

/* -------------------------------------------------------------------------- */
/* Turn player                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How long to wait for the server to move the phase on before assuming the
 * submission did not land. A valid ranking moves the room immediately — to
 * `lock-countdown`, or straight to `reveal` when every connected predictor has
 * already locked in (SPEC §2.3 phase 3) — so anything approaching this is a
 * failure, and which of the two phases lands is not this screen's business.
 */
const SUBMIT_WATCHDOG_MS = 5_000;

function RankSide() {
  const players = useRankablePlayers();
  const question = useQuestion();
  const submitRanking = useSubmitRanking();
  const roomCode = useRoomCode();
  const turn = useTurn();
  const turnIndex = turn?.index ?? null;
  const error = useRoomError();

  /**
   * In flight, NOT "done". The server is authoritative and can reject a ranking
   * — most plausibly when the host removes a player between this phone's
   * snapshot and the submission. Treating the send as final would leave the
   * board inert forever and deadlock a room of twelve people on one phone that
   * has stopped responding. So this only ever suppresses a double-tap, and it
   * is released on any server error and by a watchdog.
   */
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);

  const errorAt = error?.at ?? null;
  useEffect(() => {
    if (errorAt === null) return;
    setSubmitting((wasSubmitting) => {
      if (wasSubmitting) setRejected(true);
      return false;
    });
  }, [errorAt]);

  useEffect(() => {
    if (!submitting) return;
    const timer = window.setTimeout(() => {
      setSubmitting(false);
      setRejected(true);
    }, SUBMIT_WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [submitting]);

  const RankingInteraction = useRankingInteraction();

  // The turn player ranks ALL players INCLUDING THEMSELVES (SPEC §2.3 phase 2).
  const candidates: RankingCandidate[] = players.map((player) => ({
    id: player.id,
    name: player.name,
    photo: player.photo,
    isSelf: player.isSelf,
    connected: player.connected,
  }));

  // Restored once, at mount, from the on-device draft (see rankingDraft.ts).
  const [initialRanking] = useState(() => loadRankingDraft(roomCode, turnIndex));
  const [dragging, setDragging] = useState<string | null>(null);

  return (
    <Screen
      eyebrow="Your turn"
      meta={dragging ? `Dragging ${dragging}` : 'Draft saved'}
      /*
        The question is the heading, but set smaller than a screen heading: at
        28px a full question runs to four lines and the list opens below the
        fold. The deck sizes it down for exactly that reason.
      */
      title={
        question ? (
          <span className="block text-[1.1875rem] leading-[1.2]">
            {question.number} · {question.text}
          </span>
        ) : undefined
      }
      testId="rank-screen"
      /*
        No footer. The prediction counter used to live here as one quiet line
        under the submit action; see the file header for why it is gone. The
        board owns its own Submit, so there is nothing left for a footer to
        hold — and the ranking gets the height back.
      */
    >
      <RankingInteraction
        key={turnIndex ?? 'no-turn'}
        candidates={candidates}
        initialRanking={initialRanking}
        disabled={submitting}
        onDraggingChange={setDragging}
        onProgress={(ranking) => saveRankingDraft(roomCode, turnIndex, ranking)}
        /*
          DESIGN §4: failure stays quiet and local. The list keeps its order and
          stays draggable; all that changes is one brick line beside Submit.
        */
        submitNotice={
          rejected ? (
            <p
              className="pb-2 text-center text-[0.8125rem] font-semibold text-brick"
              role="alert"
              data-testid="rank-rejected"
            >
              Couldn&rsquo;t submit · Try again
            </p>
          ) : null
        }
        onSubmit={(ranking) => {
          setRejected(false);
          setSubmitting(true);
          // The draft is deliberately NOT cleared here: if the server rejects
          // this, the draft is what a reload would need to recover from.
          submitRanking(ranking);
        }}
      />
    </Screen>
  );
}

/* -------------------------------------------------------------------------- */
/* Everyone else                                                               */
/* -------------------------------------------------------------------------- */

function PredictSide() {
  const players = useRankablePlayers();
  const question = useQuestion();

  if (players.length === 0 || !question) {
    return (
      <Screen testId="predict-screen">
        <WaitingFor headline="Waiting for the question." />
      </Screen>
    );
  }

  // The same flow the lock countdown renders, minus the strip: phase 3 adds
  // chrome to this screen rather than replacing it — and may not happen at all,
  // in which case this screen is the last one before the reveal (DESIGN §3.5).
  return (
    <PredictionFlow>
      <SkipOffer />
    </PredictionFlow>
  );
}
